import React, { Component, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JsmCustomDomainLozenge } from './help-center/jsm-custom-domain-lozenge/index.tsx';

type Props = {
	image: ReactNode;
	label: string;
	description: ReactNode;
	button: ReactNode | undefined;
	channelName: string;
	lozengeStatus?: string; // remove when cleaning getWillShowNav4
	statusLozenge: ReactNode | undefined; // remove when cleaning getWillShowNav4
};

type State = {
	isHovered: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class ChannelItemComponent extends Component<Props, State> {
	static defaultProps = {
		button: undefined,
	};

	state = {
		isHovered: false,
	};

	onHover = () => {
		this.setState({ isHovered: true });
	};

	onBlur = () => {
		this.setState({ isHovered: false });
	};

	render() {
		const { button, image, label, description, channelName, statusLozenge } = this.props;
		const { isHovered } = this.state;
		return getWillShowNav4() ? (
			<Box
				xcss={wrapperStyles}
				testId={`servicedesk.channels-jsd.channels.${channelName}`}
				onFocus={this.onHover}
				onBlur={this.onBlur}
				onMouseOver={this.onHover}
				onMouseLeave={this.onBlur}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(fg('jsm_aiiy_for_nav_item_channel') ? {} : { tabIndex: 0 })}
			>
				<Inline alignBlock="center" grow="fill">
					<Box xcss={iconStyles}>{image}</Box>
					<Box xcss={descriptionStyles}>
						<Box xcss={labelStyles}>
							<Text size="medium" weight="medium" color="color.text.subtle">
								{label}
							</Text>
							{channelName === 'help-center' && (
								<Box xcss={helpCenterLozengeStyles}>
									<JsmCustomDomainLozenge />
								</Box>
							)}
						</Box>
						<Text as="p" maxLines={1} size="small" color="color.text.subtle">
							{description}
						</Text>
					</Box>
					<Box xcss={lozengeStyles}>
						{fg('jsm_aiiy_for_nav_item_channel') ? (
							<>
								<Box
									testId={`servicedesk.channels-jsd.channels.${channelName}.button`}
									xcss={[
										buttonStyles,
										!isHovered && fg('jsm_aiiy_for_nav_item_channel') ? hiddenStyles : undefined,
									]}
								>
									{button}
								</Box>
								{!isHovered && <>{statusLozenge}</>}
							</>
						) : (
							<>
								{isHovered ? (
									<Box
										testId={`servicedesk.channels-jsd.channels.${channelName}.button`}
										xcss={buttonStyles}
									>
										{button}
									</Box>
								) : (
									<>{statusLozenge}</>
								)}
							</>
						)}
					</Box>
				</Inline>
			</Box>
		) : (
			<ChannelItemWrapperNav3
				data-testid={`servicedesk.channels-jsd.channels.${channelName}`}
				onFocus={this.onHover}
				onBlur={this.onBlur}
				onMouseOver={this.onHover}
				onMouseLeave={this.onBlur}
				isHovered={isHovered}
			>
				<ChannelItemImageWrapperNav3>{image}</ChannelItemImageWrapperNav3>
				<ChannelItemTextWrapperNav3>
					{channelName === 'help-center' ? (
						<ChannelItemLabelWrapperNav3>
							<Heading size="xxsmall" as="h4">
								{label}
							</Heading>
							<Box xcss={channelItemLozengeWrapperStyles}>
								<JsmCustomDomainLozenge />
							</Box>
						</ChannelItemLabelWrapperNav3>
					) : (
						<Heading size="xxsmall" as="h4">
							{label}
						</Heading>
					)}

					<Text as="p" maxLines={1}>
						{description}
					</Text>
				</ChannelItemTextWrapperNav3>
				<ChannelItemButtonWrapperNav3
					data-testid={`servicedesk.channels-jsd.channels.${channelName}.button`}
					isHovered={isHovered}
				>
					{button}
				</ChannelItemButtonWrapperNav3>
				<StatusLozengeWrapper isHovered={isHovered}>{statusLozenge}</StatusLozengeWrapper>
			</ChannelItemWrapperNav3>
		);
	}
}

type ChannelItemButtonWrapperProps = {
	isHovered?: boolean;
};

export const ChannelItemView = ChannelItemComponent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChannelItemImageWrapperNav3 = styled2.div({
	minHeight: token('space.600'),
	minWidth: token('space.600'),
	borderRadius: token('space.600'),
	justifyContent: 'center',
	alignItems: 'center',
	marginTop: token('space.100'),
	marginLeft: token('space.200'),
	marginRight: token('space.200'),
	marginBottom: token('space.100'),
	backgroundColor: token('color.background.neutral'),
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChannelItemTextWrapperNav3 = styled2.div({
	marginBottom: token('space.200'),
	marginTop: token('space.200'),
	verticalAlign: 'middle',
	zIndex: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${29 * gridSize}px`,
});

const channelItemLozengeWrapperStyles = xcss({
	marginLeft: 'space.100',
	marginBottom: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChannelItemLabelWrapperNav3 = styled2.div({
	margin: 0,
	color: token('color.text.subtle'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChannelItemButtonWrapperNav3 = styled2.div<ChannelItemButtonWrapperProps>({
	height: token('space.400'),
	marginTop: token('space.200'),
	marginBottom: token('space.200'),
	marginRight: token('space.200'),
	marginLeft: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ isHovered }) => (isHovered ? 1 : 0),
	textDecoration: 'none',
	position: 'absolute',
	right: 0,
	zIndex: 10,
	backgroundColor: token('color.background.accent.gray.subtlest'),
	boxShadow: token('elevation.shadow.overlay'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusLozengeWrapper = styled2.div<{ isHovered?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ isHovered }) => (isHovered ? 0 : 1),
	marginInlineStart: 'auto',
	marginInlineEnd: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChannelItemWrapperNav3 = styled2.div<{ isHovered?: boolean }>({
	border: 0,
	padding: 0,
	display: 'flex',
	alignItems: 'center',
	width: '448px',
	height: '64px',
	overflow: 'visible',
	position: 'relative',
	marginTop: 0,
	marginRight: token('space.negative.200'),
	marginBottom: 0,
	marginLeft: token('space.negative.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isHovered }) =>
		isHovered
			? token('color.background.neutral.subtle.hovered')
			: token('color.background.neutral.subtle'),
});

const wrapperStyles = xcss({
	display: 'flex',
	position: 'relative',
	height: '48px',
	marginInline: 'space.negative.100',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral',
	},
	':focus': {
		backgroundColor: 'color.background.neutral',
	},
});

const labelStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const descriptionStyles = xcss({
	marginInlineEnd: 'space.200',
});

const iconStyles = xcss({
	display: 'flex',
	marginInlineStart: 'space.200',
	marginInlineEnd: 'space.100',
});

const buttonStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	right: 'space.0',
	height: 'space.400',
	margin: 'space.100',
	marginInlineStart: 'space.300',
	textDecoration: 'none',
	zIndex: 'layer',
	backgroundColor: 'color.background.accent.gray.subtlest',
});

const helpCenterLozengeStyles = xcss({
	marginLeft: 'space.100',
});

const lozengeStyles = xcss({
	marginInlineStart: 'auto',
	marginInlineEnd: 'space.200',
});

const hiddenStyles = xcss({
	opacity: 0,
});
